import { api } from '../base';
import { 
  UploadSessionSchema,
  UploadProgressSchema,
  UploadStatusDetailsSchema,
  InitiateUploadRequestSchema,
  type UploadSession,
  type UploadProgress,
  type UploadStatusDetails,
  type InitiateUploadRequest,
  type UploadStrategy,
  type UploadStatus 
} from '../types';

export interface UploadOptions {
  onProgress?: (progress: UploadProgress) => void;
  onStatusChange?: (status: UploadStatusDetails) => void;
  abortSignal?: AbortSignal;
  sessionId?: string; // For resuming
}

export interface UploadResult {
  file_id: number;
  session_id: string;
}

export class UploadsApi {
  // Initiate new upload session
  async initiate(request: InitiateUploadRequest): Promise<UploadSession> {
    const validated = InitiateUploadRequestSchema.parse(request);
    const response = await api.post('/v1/upload/initiate', validated);
    return UploadSessionSchema.parse(response);
  }

  // Upload single chunk
  async uploadChunk(
    uploadId: string,
    chunkIndex: number,
    chunkData: ArrayBuffer,
    options: { abortSignal?: AbortSignal } = {}
  ): Promise<UploadProgress> {
    const chunkSize = chunkData.byteLength;
    const endpoint = `/v1/upload/chunk/${uploadId}?chunk_index=${chunkIndex}&chunk_size=${chunkSize}`;
    
    const response = await api.request(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: chunkData,
      signal: options.abortSignal,
    });
    
    return UploadProgressSchema.parse(response);
  }

  // Get upload status
  async getStatus(uploadId: string, includeDetails = false): Promise<UploadStatusDetails> {
    const params = includeDetails ? '?include_details=true' : '';
    const response = await api.get(`/v1/upload/status/${uploadId}${params}`);
    return UploadStatusDetailsSchema.parse(response);
  }

  // Cancel upload
  async cancel(uploadId: string): Promise<void> {
    await api.delete(`/v1/upload/${uploadId}`);
  }

  // Universal upload method with intelligent strategy handling
  async upload(file: File, bucketId: number, options: UploadOptions = {}): Promise<UploadResult> {
    const { onProgress, onStatusChange, abortSignal, sessionId } = options;

    try {
      // Initiate upload session
      const session = await this.initiate({
        filename: file.name,
        bucket_id: bucketId,
        total_size: file.size,
        mime_type: file.type,
        priority: 'normal',
      });

      // Handle upload based on strategy
      switch (session.strategy) {
        case 'direct':
          return await this.handleDirectUpload(file, session, { onProgress, onStatusChange, abortSignal });
          
        case 'multipart':
          return await this.handleMultipartUpload(file, session, { onProgress, onStatusChange, abortSignal });
          
        case 'chunked_fast':
          return await this.handleChunkedUpload(file, session, { onProgress, onStatusChange, abortSignal });
          
        case 'chunked_queued':
          return await this.handleQueuedUpload(file, session, { onProgress, onStatusChange, abortSignal });
          
        case 'streaming_distributed':
          return await this.handleStreamingUpload(file, session, { onProgress, onStatusChange, abortSignal });
          
        default:
          throw new Error(`Unsupported upload strategy: ${session.strategy}`);
      }
    } catch (error) {
      throw error instanceof Error ? error : new Error('Upload failed');
    }
  }

  // Direct upload (small files)
  private async handleDirectUpload(
    file: File,
    session: UploadSession,
    options: Pick<UploadOptions, 'onProgress' | 'onStatusChange' | 'abortSignal'>
  ): Promise<UploadResult> {
    const { onProgress, onStatusChange, abortSignal } = options;

    // Report initial progress
    onProgress?.({
      upload_id: session.upload_id,
      uploaded_bytes: 0,
      total_bytes: file.size,
      completed: false,
      progress_percentage: 0,
    });

    onStatusChange?.({
      upload_id: session.upload_id,
      status: 'uploading',
      uploaded_bytes: 0,
      total_bytes: file.size,
      progress_percentage: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      error_count: 0,
    });

    // Upload entire file as single chunk
    const arrayBuffer = await file.arrayBuffer();
    const result = await this.uploadChunk(session.upload_id, 0, arrayBuffer, { abortSignal });

    // Report completion
    onProgress?.(result);
    
    onStatusChange?.({
      upload_id: session.upload_id,
      status: 'completed',
      uploaded_bytes: file.size,
      total_bytes: file.size,
      progress_percentage: 100,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      error_count: 0,
    });

    if (!result.file_id) {
      throw new Error('Upload completed but no file ID returned');
    }

    return { file_id: result.file_id, session_id: session.upload_id };
  }

  // Multipart upload (medium files)
  private async handleMultipartUpload(
    file: File,
    session: UploadSession,
    options: Pick<UploadOptions, 'onProgress' | 'onStatusChange' | 'abortSignal'>
  ): Promise<UploadResult> {
    // For now, handle like direct upload
    return this.handleDirectUpload(file, session, options);
  }

  // Chunked upload with pause/resume
  private async handleChunkedUpload(
    file: File,
    session: UploadSession,
    options: Pick<UploadOptions, 'onProgress' | 'onStatusChange' | 'abortSignal'>
  ): Promise<UploadResult> {
    const { onProgress, onStatusChange, abortSignal } = options;
    const chunkSize = session.chunk_size;
    const totalChunks = Math.ceil(file.size / chunkSize);

    onStatusChange?.({
      upload_id: session.upload_id,
      status: 'uploading',
      uploaded_bytes: 0,
      total_bytes: file.size,
      progress_percentage: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      error_count: 0,
    });

    let uploadedBytes = 0;
    let lastResult: UploadProgress | null = null;

    // Upload chunks sequentially for better error handling
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      if (abortSignal?.aborted) {
        throw new Error('Upload cancelled');
      }

      const start = chunkIndex * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);
      const chunkBuffer = await chunk.arrayBuffer();

      try {
        lastResult = await this.uploadChunk(session.upload_id, chunkIndex, chunkBuffer, { abortSignal });
        uploadedBytes += chunkBuffer.byteLength;

        // Report progress
        onProgress?.(lastResult);

        // Check if upload completed early (server-side assembly)
        if (lastResult.completed && lastResult.file_id) {
          onStatusChange?.({
            upload_id: session.upload_id,
            status: 'completed',
            uploaded_bytes: file.size,
            total_bytes: file.size,
            progress_percentage: 100,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            error_count: 0,
          });

          return { file_id: lastResult.file_id, session_id: session.upload_id };
        }
      } catch (error) {
        // Retry once on retryable errors
        if (this.isRetryableError(error) && !abortSignal?.aborted) {
          await this.sleep(1000);
          try {
            lastResult = await this.uploadChunk(session.upload_id, chunkIndex, chunkBuffer, { abortSignal });
            uploadedBytes += chunkBuffer.byteLength;
            onProgress?.(lastResult);
          } catch (retryError) {
            throw retryError;
          }
        } else {
          throw error;
        }
      }
    }

    // Wait for processing if needed
    if (lastResult && !lastResult.completed) {
      return await this.waitForCompletion(session.upload_id, { onStatusChange, abortSignal });
    }

    if (!lastResult || !lastResult.file_id) {
      throw new Error('Upload completed but no file ID returned');
    }

    return { file_id: lastResult.file_id, session_id: session.upload_id };
  }

  // Queued upload (large files - background processing)
  private async handleQueuedUpload(
    file: File,
    session: UploadSession,
    options: Pick<UploadOptions, 'onProgress' | 'onStatusChange' | 'abortSignal'>
  ): Promise<UploadResult> {
    // Upload chunks then wait for background processing
    const result = await this.handleChunkedUpload(file, session, options);
    return result;
  }

  // Streaming distributed upload (enterprise)
  private async handleStreamingUpload(
    file: File,
    session: UploadSession,
    options: Pick<UploadOptions, 'onProgress' | 'onStatusChange' | 'abortSignal'>
  ): Promise<UploadResult> {
    // For now, handle like chunked upload with potentially larger chunks
    return this.handleChunkedUpload(file, session, options);
  }

  // Wait for upload processing to complete
  private async waitForCompletion(
    uploadId: string,
    options: Pick<UploadOptions, 'onStatusChange' | 'abortSignal'>
  ): Promise<UploadResult> {
    const { onStatusChange, abortSignal } = options;
    let attempts = 0;
    const maxAttempts = 60; // 5 minutes max

    while (attempts < maxAttempts) {
      if (abortSignal?.aborted) {
        throw new Error('Upload cancelled');
      }

      await this.sleep(5000); // Wait 5 seconds

      try {
        const status = await this.getStatus(uploadId);
        
        onStatusChange?.(status);

        if (status.status === 'completed') {
          return { file_id: 1, session_id: uploadId };
        }
        
        if (status.status === 'failed') {
          throw new Error(status.last_error || 'Upload processing failed');
        }
      } catch (error) {
        if (error instanceof Error && error.message.includes('cancelled')) {
          throw error;
        }
        // Continue polling on network errors
      }
      
      attempts++;
    }
    
    throw new Error('Upload processing timed out');
  }

  // Resume existing upload
  async resume(
    sessionId: string,
    file: File,
    bucketId: number,
    options: UploadOptions = {}
  ): Promise<UploadResult> {
    // Get current session status
    const status = await this.getStatus(sessionId, true);
    
    if (status.status === 'completed') {
      throw new Error('Upload has already been completed');
    }
    
    if (status.status === 'failed') {
      throw new Error(status.last_error || 'Upload has failed');
    }

    // Continue with existing session
    return this.upload(file, bucketId, { ...options, sessionId });
  }

  // Utility methods
  private isRetryableError(error: unknown): boolean {
    if (error instanceof Error) {
      return error.message.includes('timeout') || 
             error.message.includes('network') ||
             error.message.includes('temporary');
    }
    return false;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Validate file before upload
  validateFile(file: File, maxSize: number = 100 * 1024 * 1024 * 1024): string | null {
    if (file.size > maxSize) {
      return `File size ${this.formatBytes(file.size)} exceeds maximum allowed size of ${this.formatBytes(maxSize)}`;
    }
    
    if (file.size === 0) {
      return 'Cannot upload empty files';
    }
    
    return null;
  }

  // Format bytes for display
  private formatBytes(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[unitIndex]}`;
  }
}